<script>
import control from "@common/control/control.vue";
import labelWrapper from "@common/control/labelWrapper.vue";
import { library } from "@common/library/library";

const editorSettings = {};

const quicklinkComposerConfig = {
    cId: "quicklinkComposer",
    cType: "quicklinkComposer",
    cTitle: "Quicklink Composer",
    cTemplate: `<div id="dialog-template-generate-quicklink">
        <div style="margin-bottom: 10px;">
            <control name="SELECT_ALL" :value="includeAllControls" @update:value="includeAllControls = $event"></control>
        </div>
        <div v-if="!includeAllControls" style="border-top: 1px solid #ccc;">
            <label-wrapper :label-settings="labelConfig"></label-wrapper>
            <div v-for="controlItem in controlsList" class="quicklink-item-wrapper">
                <div class="control-item-label">{{controlItem.cId}}</div>
                <div v-if="controlItem.controlConfig">
                    <control :name="controlItem.controlConfig.name" :value="quicklinkSelections[controlItem.controlConfig.cId].controls" @update:value="quicklinkSelections[controlItem.controlConfig.cId].controls = $event" :prop-config="controlItem.controlConfig"></control>
                </div>
                <div v-if="controlItem.propConfig">
                    <control :name="controlItem.propConfig.name" :value="quicklinkSelections[controlItem.propConfig.cId]" @update:value="quicklinkSelections[controlItem.propConfig.cId] = $event" :prop-config="controlItem.propConfig"></control>
                </div>
            </div>
        </div>
        <div>
             <control name="DOMAIN_URL" :value="domainUrl" @update:value="domainUrl = $event"></control>
        </div>
        <div class="jira-ticket-container">
            <control name="JIRA_TICKET_URL" :value="jiraTicketUrl" @update:value="jiraTicketUrl = $event"></control>
        </div>
        <div class="generate-link_btn"><control name="GENERATE_BUTTON"></control></div><br/><br/>
        <div class="generated-link-container" v-if="generatedLink">
            <p class="generated-link-text">{{generatedLink}}</p>
            <div class="generated-link-actions">
                <button type="button" @click="copyUrl" class="quicklink-button">{{ isCopied ? "Copied!" : "Copy" }}</button>
                <button type="button" @click="copyAndReloadUrl" class="quicklink-button">Reload</button>
            </div>
        </div>
    </div>`,
    controls: [
        {
            name: 'SELECT_ALL',
            id: 'useDefaultGeneratedProps',
            label: 'Include All Controls',
            type: 'boolean',
            default: true,
            removeOnDestroy: true,
            helpText: 'If enabled, each control with a changed value will be added to the link. Otherwise you can choose which controls to add to your link.'
        },
        {
            name: 'DOMAIN_URL',
            id: 'domainUrlControl',
            label: 'Environment',
            type: 'radio',
            default: 'current',
            items: [
                { label: 'Current Environment', value: 'current' },
                { label: 'https://devdash.samaritan.com/', value: 'https://devdash.samaritan.com/' },
                { label: 'https://devdashqa.samaritan.com/', value: 'https://devdashqa.samaritan.com/' },
                { label: 'localhost/', value: 'http://localhost:1024/' }
            ],
            removeOnDestroy: true
        },
        {
            name: 'GENERATE_BUTTON',
            id: 'generateButtonControl',
            label: 'Generate Link', // This will be dynamically overridden
            cssClass: 'generate-link_ctrl',
            type: 'button',
            onclick: 'generateLink',
            removeOnDestroy: true
        },
        {
            name: 'JIRA_TICKET_URL',
            id: 'jiraTicketUrlControl',
            label: 'Add Jira URL',
            cssClass: 'jira-ticket-url',
            type: 'string',
            removeOnDestroy: true,
            helpText: '<span>e.g. https://samaritan.atlassian.net/browse/DEVD-1</span>'
        }
    ]
};

const quickLinkComposeCmp = {
    name: 'quicklinkComposer',
    editorSettings,
    props: {
        quicklinkObj: {
            type: Object
        },
    },
    data() {
        return {
            generatedLink: false,
            isCopied: false,
            quicklinkSelections: {},
            domainUrl: 'current',
            origin: null,
            jiraTicketUrl: '',
            includeAllControls: true,
            controlsList: [],
            isEditMode: false, // <-- New property to track edit state
            labelConfig: {
                noInputLabel: true,
                id: 'controlsLabel',
                label: 'Controls',
                helpText: 'Select the component properties and/or controls you want to include in your quicklink.'
            }
        };
    },
    methods: {
        getCurrentDomainUrl() {
            const { origin, pathname, search } = window.location;
            const urlParams = new URLSearchParams(search);
            this.origin = origin;
            const isProd = urlParams.has('isprod');
            const pathString = `${origin}${pathname}`;
            if (isProd) {
                return `${pathString}?isprod`;
            }
            return pathString;
        },
        createControls() {
            let output = [];
            let index = 0;
            // Service to break down controls property
            let addProps = (prop, value, items, isControl) => {
                let propTypeLabel = isControl ? 'Control' : 'Data';
                items.push({
                    label: `${propTypeLabel}: ${prop} (${value})`,
                    value: prop
                });
            };
            // Create each checkbox list using state properties and control values as checkbox values/labels
            for (let cId in this.quicklinkObj) {
                if (cId === 'length') continue;
                output[index] = {
                    cId,
                    controlConfig: false, // added below
                    propConfig: false // added below
                };
                let dataItems = [];
                let controlItems = [];
                this.quicklinkSelections[cId] = {}; // add cId to selections
                // Check for controls
                if (this.quicklinkObj[cId].controls) {
                    this.quicklinkSelections[cId].controls = {};
                    for (let prop in this.quicklinkObj[cId].controls) {
                        if (prop === 'length') continue;
                        this.quicklinkSelections[cId].controls[prop] = false;
                        let value = this.quicklinkObj[cId].controls[prop];
                        addProps(prop, value, controlItems, true);
                    }
                }
                // Check for data
                for (let prop in this.quicklinkObj[cId]) {
                    if (prop === 'length') continue;
                    if (prop !== 'controls') {
                        this.quicklinkSelections[cId][prop] = false;
                        let value = this.quicklinkObj[cId][prop];
                        addProps(prop, value, dataItems);
                    }
                }
                if (dataItems.length) {
                    output[index].propConfig = {
                        label: cId,
                        name: 'component_props_' + cId,
                        type: 'CHECKBOX_LIST',
                        id: 'controls_' + cId,
                        hideLabel: true,
                        cssClass: 'quicklink-checkbox-list',
                        cId,
                        items: dataItems,
                        removeOnDestroy: true
                    };
                }
                if (controlItems.length) {
                    output[index].controlConfig = {
                        label: cId,
                        name: 'component_controls_' + cId,
                        type: 'CHECKBOX_LIST',
                        id: 'controls_' + cId,
                        hideLabel: true,
                        cssClass: 'quicklink-checkbox-list',
                        cId,
                        items: controlItems,
                        removeOnDestroy: true
                    };
                    this.quicklinkSelections[cId].controls = {};
                }
                index++;
            }
            this.controlsList = output;
        },
        copyUrl() {
            library.copyTextToClipboard(this.generatedLink);
            this.isCopied = true;
            setTimeout(() => {
                this.isCopied = false;
            }, 3000);
        },
        copyAndReloadUrl() {
            library.copyTextToClipboard(this.generatedLink);
            window.location.href = this.generatedLink;
        },
        async generateLink() {
            let includedControls = !this.includeAllControls && this.quicklinkSelections;
            if (!await this.isJiraTicketUrlValid()) return;
            const baseUrl = this.domainUrl === 'current'
                ? this.getCurrentDomainUrl()
                : this.domainUrl;
            this.generatedLink = await library.getQuickLink(false, false, includedControls, baseUrl, this.jiraTicketUrl);
        },
        async isJiraTicketUrlValid() {
            let isValid = true;
            if (this.jiraTicketUrl) {
                if (this.jiraTicketUrl.indexOf('https://samaritan.atlassian.net/browse/') === -1) {
                    library.showDialog('Incorrect Jira URL', false, false, { title: 'Warning', showAsAlert: true });
                    isValid = false;
                }
            }
            return isValid;
        }
    },
    mounted() {
        let mountInterval = setInterval(() => {
            if (this.quicklinkObj) {
                clearInterval(mountInterval);
                this.createControls();
            }
        }, 100);
    },
    components: {
        control,
        labelWrapper
    },
};
componentStore.initComponent(quickLinkComposeCmp, quicklinkComposerConfig);
export { quicklinkComposerConfig, quickLinkComposeCmp };
export default quickLinkComposeCmp;
</script>

<style scoped>
.control-item-label {
    font-size: var(--text-size-base);
    color: var(--text-color-subtitle);
}
.quicklink-checkbox-list {
    margin: 0;
}
.quicklink-checkbox-list :deep(.checkboxes-container) {
    margin: 0;
}
:deep(.jira-ticket-container > div) {
    display: flex !important;
    margin-bottom: var(--space-9);
    gap: var(--space-6);
    margin-right: var(--space-6);
}
:deep(.jira-ticket-container > div > div:has(.jira-ticket-url)) {
    flex-grow: 1;
    margin-right: var(--space-6);
}
.generate-link_btn :deep(.e-btn.generate-link_ctrl) {
    background: var(--surface-subtle-primary);
    border-radius: var(--radius-xs);
    box-shadow: var(--shadow-xs);
    color: var(--text-color-body);
    padding: var(--space-4) var(--space-8);
    border: 1px solid #b0c4de;
    font-weight: var(--text-weight-medium);
    width: auto;
}
.generate-link_btn :deep(.e-btn.generate-link_ctrl:hover),
.generate-link_btn :deep(.e-btn.generate-link_ctrl:focus),
.generate-link_btn :deep(.e-btn.generate-link_ctrl:focus-visible) {
    background: var(--primary-100);
}
.quicklink-item-wrapper {
    margin: var(--space-6) 0;
}
.generated-link-container {
    border-radius: var(--radius-2xs);
    background: var(--surface-subtle);
    border: 1px solid var(--grayscale-300);
    padding: var(--space-6) var(--space-8);
}
.generated-link-text {
    color: var(--text-color-body);
    font-size: calc(var(--text-size-base) + 1px);
    flex-grow: 1;
    margin: 0;
    padding-bottom: var(--space-2);
    word-break: break-all;
}
.generated-link-actions {
    display: flex;
    gap: var(--space-6);
    margin-top: var(--space-5);
    justify-content: flex-end;
}
.quicklink-button {
    border: 1px solid var(--border-color-disabled);
    border-radius: var(--radius-2xs);
    box-shadow: var(--shadow-xs);
    padding: var(--space-3) var(--space-5);
    color: var(--text-color-body);
    font-size: var(--text-size-base);
    background: var(--surface-default);
    text-decoration: none;
    cursor: pointer;
    transition: background-color 0.2s ease;
}
.quicklink-button:hover,
.quicklink-button:focus,
.quicklink-button:focus-visible                  {
    background-color: var(--grayscale-200);
}
</style>
